import {
	Loader2,
	MapPinOffIcon,
	SearchIcon,
	StethoscopeIcon,
	X,
} from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	rankTherapistSearchResults,
	type SearchOrigin,
} from "@/lib/therapist-scheduling/search-ranking";
import { cn } from "@/lib/utils";
import type { TherapistFeasible } from "@/types/admin-portal/therapist";

/** Result row from GET /api/v1/therapists/search — same shape the feasible
 *  endpoint returns, so a consumer can persist the whole object as-is. */
export type TherapistSearchResult = TherapistFeasible;

/** ALL / KARPIS / FLAT, labelled from common.employment_types (FLAT reads "Mitra"). */
const EMPLOYMENT_TYPE_FILTERS = ["ALL", "KARPIS", "FLAT"] as const;

type TherapistSearchListProps = {
	/** Reschedule/bulk scope: search is bound to an existing appointment. */
	appointmentId?: string | null;
	/** Create-booking scope: search is bound to location + service + date. */
	locationId?: string | null;
	serviceId?: string | null;
	appointmentDateTime?: Date | string | null;
	preferredTherapistGender?: string | null;
	isAllOfDay?: boolean;
	/** Where the Visit is served. Ranks results by Region and distance and flags
	 *  therapists outside it; without it results keep the server's order. */
	origin?: SearchOrigin | null;
	/** Currently-assigned therapist, for the highlighted row. */
	selectedTherapistId?: string | number | null;
	onSelect: (therapist: TherapistSearchResult) => void;
	/** Unique per instance (bulk renders one per visit row). */
	instanceId?: string;
	className?: string;
};

function toIsoOrEmpty(value?: Date | string | null): string {
	if (!value) return "";
	if (value instanceof Date) return value.toISOString();
	return value;
}

/**
 * The shared "find therapist" search UI: an employment-type filter, a debounced
 * name search, and a city-grouped result list that commits a therapist on click
 * (no separate Apply). Extracted from bulk reschedule so single reschedule and
 * create booking render the exact same field + list. Date/time is picked by the
 * caller's own DateTimePicker — this only assigns the therapist.
 */
export function TherapistSearchList({
	appointmentId,
	locationId,
	serviceId,
	appointmentDateTime,
	preferredTherapistGender,
	isAllOfDay = false,
	origin,
	selectedTherapistId,
	onSelect,
	instanceId = "0",
	className,
}: TherapistSearchListProps) {
	const { t, i18n } = useTranslation("appointments-form");
	const kmFormat = useMemo(
		() =>
			new Intl.NumberFormat(i18n.language, {
				minimumFractionDigits: 1,
				maximumFractionDigits: 1,
			}),
		[i18n.language],
	);
	const [employmentType, setEmploymentType] = useState<string>("ALL");
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [therapists, setTherapists] = useState<TherapistSearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);

	const deferredSearchQuery = useDeferredValue(searchQuery);

	const groupedTherapists = useMemo(
		() =>
			rankTherapistSearchResults(
				therapists,
				origin,
				t("common.not_applicable", "N/A"),
			),
		[therapists, origin, t],
	);

	// Primitives, not `origin`: callers pass a fresh object every render, and the
	// effect below would refetch on each one.
	const originLatitude = origin?.latitude;
	const originLongitude = origin?.longitude;

	// Debounced fetch. An empty query clears the list (matching the search UIs).
	// `cancelled` drops a stale in-flight response (and its loading flag) when the
	// query/filters change or the component unmounts, so a slow request can never
	// overwrite newer results.
	useEffect(() => {
		const query = deferredSearchQuery.trim();
		if (!query) {
			setTherapists([]);
			setIsSearching(false);
			return;
		}

		let cancelled = false;
		setIsSearching(true);
		const timeoutId = setTimeout(() => {
			const params = new URLSearchParams({
				search_query: query,
				employment_type: employmentType,
				is_all_of_day: isAllOfDay.toString(),
				preferred_therapist_gender: preferredTherapistGender ?? "NO PREFERENCE",
				location_id: appointmentId ? "" : (locationId ?? ""),
				service_id: appointmentId ? "" : (serviceId ?? ""),
				appointment_date_time: appointmentId
					? ""
					: toIsoOrEmpty(appointmentDateTime),
			});
			if (appointmentId) params.set("appointment_id", appointmentId);
			if (originLatitude && originLongitude) {
				params.set("latitude", String(originLatitude));
				params.set("longitude", String(originLongitude));
			}

			fetch(`/api/v1/therapists/search?${params.toString()}`, {
				headers: { Accept: "application/json" },
			})
				.then((res) => res.json())
				.then((data) => {
					if (!cancelled && data.therapists) setTherapists(data.therapists);
				})
				.catch((err) => {
					if (!cancelled) console.error("Failed to fetch therapists:", err);
				})
				.finally(() => {
					if (!cancelled) setIsSearching(false);
				});
		}, 300);

		return () => {
			cancelled = true;
			clearTimeout(timeoutId);
		};
	}, [
		deferredSearchQuery,
		employmentType,
		isAllOfDay,
		preferredTherapistGender,
		appointmentId,
		locationId,
		serviceId,
		appointmentDateTime,
		originLatitude,
		originLongitude,
	]);

	const cityCount = groupedTherapists.length;

	return (
		<div className={cn("space-y-4", className)}>
			<div className="flex flex-col md:flex-row items-start justify-between gap-4 p-3 border rounded-md border-border bg-sidebar">
				<div>
					<p className="text-sm font-semibold">
						{t("therapists.filters.employment_type_title")}
					</p>
					<p className="text-xs text-muted-foreground text-pretty">
						{t("therapists.filters.employment_type_description")}
					</p>
				</div>
				<div className="flex gap-2">
					{EMPLOYMENT_TYPE_FILTERS.map((value) => (
						<Button
							key={value}
							type="button"
							variant={employmentType === value ? "default" : "outline"}
							size="sm"
							onClick={() => setEmploymentType(value)}
						>
							{t(`common.employment_types.${value}`)}
						</Button>
					))}
				</div>
			</div>

			<div className="space-y-2 !mt-6">
				<Label
					htmlFor={`therapist-search-input-${instanceId}`}
					className="sr-only"
				>
					{t("therapists.filters.search_placeholder", "Search Therapist")}
				</Label>
				<div className="relative">
					<SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						id={`therapist-search-input-${instanceId}`}
						placeholder={t(
							"therapists.filters.search_placeholder",
							"Search by name, registration number or email...",
						)}
						className="bg-input/50 pl-9 pr-9"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						onPointerDown={(e) => e.stopPropagation()}
					/>
					{searchQuery && (
						<button
							type="button"
							className="absolute right-2 top-2.5"
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								setSearchQuery("");
								setTherapists([]);
							}}
						>
							<X className="h-4 w-4 text-muted-foreground" />
						</button>
					)}
				</div>
			</div>

			<div className="space-y-4">
				<div className="w-full">
					<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/75 flex items-center gap-2">
						{therapists.length > 0
							? t("therapists.filters.found_count", {
									count: therapists.length,
									cityCount,
									defaultValue: `${therapists.length} therapists found across ${cityCount} cities`,
								})
							: t(
									"therapists.filters.search_instruction",
									"Search for therapists",
								)}
						<span className="h-[2px] flex-1 bg-border" />
					</span>
					{therapists.length > 0 && (
						<p className="mt-1 text-xs text-muted-foreground text-pretty">
							{t("common.therapist_search.unchecked_note")}
						</p>
					)}
				</div>

				<div className="max-h-[250px] md:max-h-[700px] overflow-y-auto border rounded-md bg-muted">
					{therapists.length === 0 && !searchQuery && (
						<div className="text-center text-sm text-muted-foreground/75 p-4 italic">
							<p>
								{t(
									"therapists.filters.search_instruction",
									"Start typing to search therapists",
								)}
							</p>
						</div>
					)}

					{therapists.length === 0 && searchQuery && isSearching && (
						<div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground/75 italic">
							<Loader2 className="size-4 animate-spin" />
							<span>{t("therapists.filters.searching", "Searching…")}</span>
						</div>
					)}

					{therapists.length === 0 && searchQuery && !isSearching && (
						<div className="text-center text-sm text-muted-foreground/75 p-4 italic">
							<p>
								{t("therapists.filters.not_found", {
									query: searchQuery,
									defaultValue: `No therapist found for "${searchQuery}"`,
								})}
							</p>
						</div>
					)}

					{therapists.length > 0 &&
						groupedTherapists.map(({ city, therapists: cityTherapists }) => (
							<div key={city}>
								<div className="px-4 py-2 bg-muted text-xs font-medium text-muted-foreground sticky top-0 z-10 border-b border-r">
									<span>{city || t("common.not_applicable", "N/A")}</span>
								</div>

								<div className="grid grid-cols-1">
									{cityTherapists.map(
										({ therapist, distanceKm, outsideRegion }) => {
											const isSelected = selectedTherapistId === therapist.id;
											return (
												<button
													key={therapist.id}
													type="button"
													className={cn(
														"w-full text-left px-4 py-3 bg-card hover:bg-accent transition-colors flex items-center gap-3 border-b",
														isSelected ? "bg-primary/5 text-primary" : "",
													)}
													onClick={() => onSelect(therapist)}
												>
													<div
														className={cn(
															"w-10 h-10 border rounded-full flex items-center justify-center flex-shrink-0",
															isSelected
																? "bg-primary text-primary-foreground border-primary"
																: "bg-muted text-muted-foreground border-border",
														)}
													>
														<StethoscopeIcon className="w-5 h-5 shrink-0" />
													</div>

													<div className="flex-1 min-w-0">
														<p className="font-medium text-sm uppercase tracking-tight truncate">
															{therapist.name}
														</p>
														{therapist.email && (
															<p className="text-xs truncate opacity-75">
																{therapist.email}
															</p>
														)}
														<p
															className={cn(
																"text-xs opacity-75",
																isSelected
																	? "text-primary"
																	: "text-muted-foreground",
															)}
														>
															<span>
																{therapist.employmentType
																	? t(
																			`common.employment_types.${therapist.employmentType}`,
																			therapist.employmentType,
																		)
																	: t("common.not_applicable", "N/A")}
															</span>
															<span className="mx-1">&bull;</span>
															<span>
																{therapist.gender
																	? t(
																			`appt_schedule.fields.pref_therapist_gender.options.${therapist.gender.toLowerCase()}`,
																			therapist.gender,
																		)
																	: t("common.not_applicable", "N/A")}
															</span>
															{distanceKm !== null && (
																<>
																	<span className="mx-1">&bull;</span>
																	<span className="tabular-nums">
																		{t("common.therapist_search.distance", {
																			km: kmFormat.format(distanceKm),
																		})}
																	</span>
																</>
															)}
														</p>
														{outsideRegion && (
															<p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-300">
																<MapPinOffIcon className="size-3.5 shrink-0" />
																{t("common.therapist_search.outside_region", {
																	city: origin?.city,
																})}
															</p>
														)}
													</div>
												</button>
											);
										},
									)}
								</div>
							</div>
						))}
				</div>
			</div>
		</div>
	);
}
